import type { BoardClimb, BoardHold, Hold } from '../../../types/climb';
import type {
  Board,
  BoardCalibration,
  BoardExtraHoldMap,
  BoardHoldMap,
  BoardHoldRadii,
  BoardShapes,
} from '../../../types/device';
import {
  holdPosition,
  holdSpecFor,
  holdWidth,
  isScannedHold,
  scannedOutline,
  scannedWallOf,
  wallHoldWidth,
  type ScannedWall,
} from './geometry';
import type { FocusRect } from '../hold-focus';
import { outlinedHold } from '../hold-outline';
import { boardRingColor } from './roles';

/**
 * Everything a board problem is drawn on: the wall's photo and the geometry
 * that puts the problem's holds on it — what `BoardGridDisplay` takes, the
 * problem's own holds included.
 */
export interface ClimbWall {
  imageUrl: string | null;
  rows: number;
  columns: number;
  calibration: BoardCalibration | null;
  holds: BoardHold[];
  /** The wall's own holds, which decide how big each one is drawn. */
  boardHolds: BoardHoldMap | null;
  /**
   * Where its off-grid holds sit: a problem points at one by id, so without
   * this the screw-ons of it are drawn in the middle of the wall rather than
   * where they are bolted.
   */
  extraHolds: BoardExtraHoldMap | null;
  /**
   * The lines a drawn wall is made of, for the same reason: there a hold's row
   * is which line it is on, so without them every hold of such a problem would
   * be drawn in the middle of the picture.
   */
  shapes: BoardShapes | null;
  holdRadii: BoardHoldRadii | null;
  /**
   * The outlines of a scanned wall's holds, which are the holds there. The
   * board's own carry the whole wall; the climb's carry its own holds, which is
   * all a problem drawn on its own needs.
   */
  scanned: ScannedWall | null;
}

/**
 * The wall a board problem is drawn on. The board it is viewed through wins (a
 * global climb shows the gym's own board photo and calibration), falling back
 * to what the climb carries of its own.
 *
 * **An archived problem is drawn on its own wall**: the board it was set on has
 * a new photo since, so what the board says now is about another wall
 * (docs/SPRAY_WALL_SCAN_PLAN.md). The climb carries the old one.
 */
export function climbWall(climb: BoardClimb, board: Board | null | undefined): ClimbWall {
  const wallBoard = climb.archived ? null : (board ?? null);

  return {
    imageUrl: wallBoard?.image_url ?? climb.board_image_url,
    rows: wallBoard?.rows ?? climb.board_rows ?? 0,
    columns: wallBoard?.columns ?? climb.board_columns ?? 0,
    calibration: wallBoard?.calibration ?? climb.board_calibration,
    holds: climb.board_holds ?? [],
    boardHolds: wallBoard?.holds ?? climb.board_holds_map ?? null,
    extraHolds: wallBoard?.extra_holds ?? climb.board_extra_holds ?? null,
    shapes: wallBoard?.shapes ?? climb.board_shapes ?? null,
    holdRadii: wallBoard?.hold_radii ?? climb.board_hold_radii ?? null,
    scanned: scannedWallOf(
      wallBoard?.scanned_holds ?? climb.board_scanned_holds,
      wallBoard ? wallBoard.image_crop : climb.board_image_crop
    ),
  };
}

/**
 * A board problem's holds as a photo climb's are kept: each one where it sits
 * on the wall's photo, as fractions of the picture, in the colour of its ring —
 * its traced outline on a scanned wall, the circle it is drawn as otherwise.
 * What lets a board problem be drawn by code that only knows photo climbs (the
 * share card, see `climb-thumbnail.ts`).
 *
 * `aspect` (the photo's width over its height) is needed twice over: a hold is
 * sized as a fraction of the width, so its height in fractions of the height
 * is that times the aspect; and a plain grid's hold size is read off its pitch,
 * which needs it too (see `gridHoldWidth`).
 */
export function boardWallHolds(wall: ClimbWall, aspect: number, holds: BoardHold[] = wall.holds): Hold[] {
  if (aspect <= 0) return [];
  const gridWidth = wallHoldWidth(wall.calibration, wall.rows, wall.columns, aspect, wall.shapes);

  return holds.map((hold) => {
    const color = boardRingColor(hold);
    const traced = isScannedHold(hold.row) ? scannedOutline(wall.scanned, hold.col) : null;
    if (traced) return outlinedHold(traced.map((point) => [point.x, point.y]), color);

    const centre = holdPosition(
      wall.calibration, wall.rows, wall.columns, wall.extraHolds, hold.row, hold.col, wall.shapes, wall.scanned
    );
    const width = holdWidth(holdSpecFor(wall.boardHolds, hold), wall.holdRadii, gridWidth);
    return { x: centre.x, y: centre.y, width, height: width * aspect, color };
  });
}

/**
 * The box around a board problem's start holds on its photo, circles included,
 * in fractions of the picture — what a thumbnail of the problem zooms to, by
 * the same reading `startHoldsBounds` gives a photo climb's circles. Falls back
 * to every hold when none is marked a start, and to nothing for no holds.
 * `aspect` as for `boardWallHolds`.
 */
export function startHoldsFrame(wall: ClimbWall, aspect: number): FocusRect | null {
  if (wall.holds.length === 0 || aspect <= 0) return null;

  const starts = wall.holds.filter((hold) => hold.role === 'start');
  const framed = boardWallHolds(wall, aspect, starts.length > 0 ? starts : wall.holds);

  const left = Math.min(...framed.map((hold) => hold.x - hold.width / 2));
  const right = Math.max(...framed.map((hold) => hold.x + hold.width / 2));
  const top = Math.min(...framed.map((hold) => hold.y - hold.height / 2));
  const bottom = Math.max(...framed.map((hold) => hold.y + hold.height / 2));

  return { x: left, y: top, width: right - left, height: bottom - top };
}
